import { Capacitor } from '@capacitor/core';
import { Purchases } from '@revenuecat/purchases-capacitor';
import { Purchases as PurchasesWeb } from '@revenuecat/purchases-js';

/**
 * Keeps RevenueCat's copy of the Mixpanel distinct id in step with the app's.
 *
 * RevenueCat's Mixpanel integration sends trial, conversion and renewal events
 * server-side. Without this attribute it files them under its own app user id
 * (a $RCAnonymousID for anyone who buys before signing up, which is most
 * trials), so in Mixpanel the trial and the person's app activity look like
 * two different people: of 269 trial starts checked on 2026-11-02, only 4 sat
 * on the profile that also held the app events. With it, RevenueCat uses the
 * id we hand it, which is already stitched to the account by aliasOnce.
 *
 * The only ids ever handed over are ones the app's own Mixpanel SDK is
 * tracking under at that moment (device id, guest token, then account id), so
 * this introduces no new identities: RevenueCat's events simply land wherever
 * the app's own events land.
 *
 * Fire-and-forget. Called after RevenueCat is configured and again on every
 * Mixpanel identify. Never throws into product code.
 */

// Web only: an anonymous visitor has no RevenueCat customer until they buy, and
// every tracked event re-identifies, so a failing send is retried a few times
// per id and then left alone rather than firing once per event.
const webAttempts = new Map<string, number>();
const maxWebAttempts = 3;
let lastWebSynced: string | null = null;

export const syncMixpanelIdToRevenueCat = (distinctId: string | undefined): void => {
  if (!distinctId) return;
  try {
    if (Capacitor.isNativePlatform()) {
      // No in-memory dedupe here on purpose: the native SDK keeps the
      // attribute in its own cache and only syncs when the value changes, and
      // after a logout RevenueCat has a fresh anonymous customer that needs
      // the id again. Rejects only if configure has not run yet.
      Purchases.setMixpanelDistinctID({ mixpanelDistinctID: distinctId }).catch(() => undefined);
      return;
    }
    if (distinctId === lastWebSynced) return;
    const tried = webAttempts.get(distinctId) ?? 0;
    if (tried >= maxWebAttempts) return;
    webAttempts.set(distinctId, tried + 1);
    if (!PurchasesWeb.isConfigured()) return;
    lastWebSynced = distinctId;
    PurchasesWeb.getSharedInstance()
      // eslint-disable-next-line @typescript-eslint/naming-convention
      .setAttributes({ $mixpanelDistinctId: distinctId })
      .catch(() => {
        if (lastWebSynced === distinctId) lastWebSynced = null;
      });
  } catch {
    // tracking must never throw into product code
  }
};
